import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { toDisplayError } from '../../utils/apiError';
import { useThemeMode, type ThemeModePreference } from '@theme/ThemeModeProvider';
import LanguageSetting from './LanguageSetting';

interface UserPreferences {
  theme?: ThemeModePreference;
  reducedMotion?: boolean;
}

interface UserProfile {
  id: string;
  preferences?: UserPreferences | null;
}

const MODES: ThemeModePreference[] = ['system', 'light', 'dark'];

/**
 * REV-491: appearance and accessibility preferences.
 *
 * Dark mode existed here once as a switch that stored a value and rendered
 * identically, and REV-445 removed it. This one drives the real MUI theme
 * through ThemeModeProvider — the preference is applied the moment it changes,
 * and only then saved.
 *
 * Applying before saving is deliberate: the user sees the result immediately,
 * and a failed save shows an error rather than silently reverting a theme they
 * are already looking at.
 *
 * Each control sends ONLY the key it owns. The API merges preferences now
 * (REV-491), but reconstructing and sending the whole bag would reintroduce the
 * clobber the moment that merge were relaxed.
 */
export default function AppearanceTab() {
  const { t } = useTranslation();
  const { preference, setPreference, reducedMotion, setReducedMotion } = useThemeMode();
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Adopt the account-level preference on first load. localStorage is only a
  // mirror so the app does not flash the wrong theme while this is in flight.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.get<{ success: boolean; data: UserProfile }>('/v1/users/me');
        if (cancelled) return;
        const prefs = res.data.preferences ?? {};
        if (prefs.theme && MODES.includes(prefs.theme)) {
          setPreference(prefs.theme);
        }
        if (typeof prefs.reducedMotion === 'boolean') {
          setReducedMotion(prefs.reducedMotion);
        }
      } catch {
        // Non-fatal: the locally mirrored preference still applies. Surfacing
        // an error here would be noise for something the user can still change.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once — re-running on every setter identity change would fight the
    // user's own selections.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useCallback(
    async (preferences: UserPreferences) => {
      setError(null);
      try {
        await apiClient.put('/v1/users/me', { preferences });
      } catch (e) {
        setError(toDisplayError(e, t('settings.appearance.saveFailed', 'Could not save.')));
      }
    },
    [t]
  );

  const onModeChange = (mode: ThemeModePreference) => {
    setPreference(mode);
    void save({ theme: mode });
  };

  const onReducedMotionChange = (on: boolean) => {
    setReducedMotion(on);
    void save({ reducedMotion: on });
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        {t('settings.appearance.title', 'Appearance & accessibility')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3} sx={{ mt: 1 }}>
        {/* REV-798 */}
        <LanguageSetting />

        <FormControl>
          <FormLabel id="theme-mode-label">{t('settings.appearance.theme', 'Theme')}</FormLabel>
          <RadioGroup
            aria-labelledby="theme-mode-label"
            value={preference}
            onChange={(e) => onModeChange(e.target.value as ThemeModePreference)}
          >
            <FormControlLabel
              value="system"
              control={<Radio />}
              label={t('settings.appearance.system', 'Match my system')}
              disabled={!loaded}
            />
            <FormControlLabel
              value="light"
              control={<Radio />}
              label={t('settings.appearance.light', 'Light')}
              disabled={!loaded}
            />
            <FormControlLabel
              value="dark"
              control={<Radio />}
              label={t('settings.appearance.dark', 'Dark')}
              disabled={!loaded}
            />
          </RadioGroup>
        </FormControl>

        <FormControl>
          <FormLabel id="motion-label">{t('settings.appearance.motion', 'Motion')}</FormLabel>
          <FormControlLabel
            control={
              <Switch
                checked={reducedMotion}
                onChange={(e) => onReducedMotionChange(e.target.checked)}
                disabled={!loaded}
                inputProps={{ 'aria-describedby': 'reduced-motion-help' }}
              />
            }
            label={t('settings.appearance.reduceMotion', 'Reduce motion')}
          />
          <Typography id="reduced-motion-help" variant="caption" color="text.secondary">
            {t(
              'settings.appearance.reduceMotionHelp',
              'Turns off interface animations. Your system setting can also switch this on, and when it does this cannot turn it off.'
            )}
          </Typography>
        </FormControl>
      </Stack>
    </Paper>
  );
}
